/**
 * @fileoverview PayDayPuffin Subprocessors disclosure registry data.
 *
 * Hand-migrated faithfully from
 * `apps/payday-web/src/portals/marketing-website/pages/SubprocessorsPage.jsx`.
 *
 * Public subprocessor disclosure required by GDPR Art. 28(4). Lists
 * every third-party service provider that processes personal data on
 * PayDayPuffin's behalf, with entity name, role, data categories, and
 * location. Authoritative list referenced from the Terms of Service
 * and Privacy Policy.
 */

/** @type {import('../../types.js').LegalDocument} */
export const subprocessors = Object.freeze({
  appKey: 'payday',
  documentKey: 'subprocessors',
  frontmatter: {
    title: 'Subprocessors',
    productName: 'PayDayPuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-04-11',
    contactEmail: 'hello@paydaypuffin.com',
    canonicalUrl: 'https://paydaypuffin.com/subprocessors',
    ogImage: 'https://paydaypuffin.com/og-image.jpg',
    description:
      'List of PayDayPuffin subprocessors: every third-party provider that processes personal data, with role, data categories, and location.',
  },
  sections: [
    {
      id: 'overview',
      title: 'Overview',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: "A subprocessor is a third-party service provider that processes personal data on {productName}'s behalf under contractual obligations that protect the confidentiality, integrity, and availability of that data. This page lists every subprocessor we currently engage, along with their role, the categories of data they process, and their location.",
        },
        {
          type: 'paragraph',
          text: "Maintaining an accurate subprocessor list is a core part of our data protection commitment. We will provide at least thirty (30) days' written notice before engaging any new subprocessor, via posting to this page and email notification to the primary account holder on record. You may object to a new subprocessor on reasonable, objective, and substantive grounds by contacting {contactEmail} within the notice period.",
        },
      ],
    },
    {
      id: 'current-subprocessors',
      title: 'Current Subprocessors',
      order: 2,
      blocks: [
        {
          type: 'subprocessor-list',
          entries: [
            {
              name: 'Gusto, Inc.',
              role: "Payroll execution — executes wage payments, withholds and files federal, state, and local taxes, makes direct deposits to your employees, and generates W-2, 1099, and pay stub documents on PayDayPuffin's behalf.",
              dataCategories: [
                'Employee identity (name, email, tax identification, home address)',
                'Compensation (wages, salaries, bonuses, deductions)',
                'Banking information for direct deposit',
                'Hours worked',
              ],
              location: 'United States (California headquarters, processing in U.S. regions).',
              legalEntity: 'Gusto, Inc. (Delaware C-corporation, EIN on file).',
              references: [
                { title: 'Gusto Privacy Policy', url: 'https://gusto.com/about/privacy' },
                { title: 'Gusto Terms of Service', url: 'https://gusto.com/about/terms' },
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'why-we-use-subprocessors',
      title: 'Why We Use Subprocessors',
      order: 3,
      blocks: [
        {
          type: 'paragraph',
          text: 'Payroll execution — including money movement, tax withholding and filing, and government reporting — is a licensed activity regulated at the federal and state levels. Rather than obtaining and maintaining every license required to execute payroll directly, {productName} partners with a licensed payroll processor (currently Gusto, Inc.) to perform the regulated operations on your behalf. This allows us to focus on the software, workflow, and user experience while ensuring your payroll is handled by a regulated entity with the appropriate licenses and insurance.',
        },
        {
          type: 'paragraph',
          text: 'You do not need a separate account with any of our subprocessors. Your {productName} subscription covers both the software and the payroll processing services, and your contractual relationship is with {productName}.',
        },
      ],
    },
    {
      id: 'security-and-contractual-obligations',
      title: 'Security and Contractual Obligations',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: 'Every subprocessor we engage is required to meet contractual obligations that include, at a minimum: industry-standard encryption at rest and in transit, restricted access controls, breach notification requirements, cooperation with data subject requests, and GDPR Art. 28 data processing terms. We maintain records of our subprocessor agreements and due diligence activities.',
        },
      ],
    },
    {
      id: 'data-processing-agreement',
      title: 'Data Processing Agreement',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: 'If your business requires a formal Data Processing Agreement (DPA) signed by {productName}, please visit our Data Processing Agreement page or contact {contactEmail} to request one.',
        },
      ],
    },
    {
      id: 'contact',
      title: 'Contact',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'Questions about our subprocessors, their role, or our data processing practices? Contact us at {contactEmail}. We aim to respond within two business days.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
